import { identify, Identify, track } from './amplitude';
import type { QualityProfile } from '../scene/qualityProfile';
import { $locale } from '../state/locale';
import { LAYOUT_MODE_CHANGED } from './events';
import { LAYOUT_DESKTOP_BREAKPOINT } from '../hooks/useLayoutMode';
import { IS_BALADY } from '../config/appConfig';

export type LayoutModeName = 'mobile' | 'desktop';

/** Поверхность деплоя. Build-флаг авторитетен (Balady-webview не должен
 *  читаться как обычный iframe); рантайм-проверка различает только
 *  iframe vs standalone. Других сигналов в коде нет — не выдумываем. */
export type DeploySurface = 'balady' | 'iframe' | 'standalone';

const isEmbedded = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin доступ к window.top кидает → значит, мы во встраивании.
    return true;
  }
};

/** Чистая резолюция surface — build-флаг приоритетнее рантайм-iframe-проверки
 *  (Balady-webview не должен читаться как обычный iframe). Вынесена для теста. */
export const resolveSurface = (
  isBalady: boolean,
  embedded: boolean,
): DeploySurface => {
  if (isBalady) return 'balady';
  if (embedded) return 'iframe';
  return 'standalone';
};

const readSurface = (): DeploySurface => resolveSurface(IS_BALADY, isEmbedded());

const readIsPreviewFrame = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    new URLSearchParams(window.location.search).get('viewMode') === 'mobile'
  );
};

const readLayoutMode = (): LayoutModeName => {
  if (typeof window === 'undefined') return 'mobile';
  // Mobile preview frame forces mobile layout regardless of window width —
  // mirrors `useLayoutMode` logic so analytics matches React-side
  // perception.
  if (readIsPreviewFrame()) return 'mobile';
  return window.innerWidth >= LAYOUT_DESKTOP_BREAKPOINT ? 'desktop' : 'mobile';
};

const setLayoutUserProperties = (properties: InstanceType<typeof Identify>): void => {
  properties.set('layout_mode', readLayoutMode());
  properties.set('viewport_width', typeof window === 'undefined' ? 0 : window.innerWidth);
  properties.set('is_preview_frame', readIsPreviewFrame());
};

export const identifyDevice = (profile: QualityProfile) => {
  const userProperties = new Identify();
  userProperties.set('device_tier', profile.tier);
  userProperties.set('device_class', profile.deviceClass);
  if (profile.benchmarkMs != null) {
    userProperties.set('gpu_benchmark_ms', profile.benchmarkMs);
  }
  if (profile.benchmarkCv != null) {
    userProperties.set('gpu_benchmark_cv', profile.benchmarkCv);
  }
  userProperties.set('locale', $locale.getState());
  // Поверхность деплоя — глобальное свойство сессии. Ставим только здесь (полный
  // identify), НЕ в setLayoutUserProperties: surface не меняется на resize,
  // незачем переотправлять на layout-only re-identify.
  userProperties.set('surface', readSurface());
  setLayoutUserProperties(userProperties);
  identify(userProperties);
};

/** Which engine draws the scene. Only the SDK is left, but a user property
 *  sticks: without the write a user last seen on legacy keeps that tag. */
export const identifyRenderEngine = () => {
  identify(new Identify().set('render_engine', 'datum-sdk'));
};

/** Refresh layout user properties — call when layoutMode flips at runtime
 *  (cross-breakpoint resize). Keeps segmentation accurate without
 *  re-running the GPU benchmark or the full device identify pipeline. */
export const identifyLayoutMode = () => {
  const userProperties = new Identify();
  setLayoutUserProperties(userProperties);
  identify(userProperties);
};

/** Emit a discrete event for cross-breakpoint resize. Rare in practice
 *  (most users don't resize between mobile/desktop widths mid-session),
 *  but the data point is useful for understanding adaptive-layout impact. */
export const layoutModeChanged = (from: LayoutModeName, to: LayoutModeName): void => {
  track(LAYOUT_MODE_CHANGED, {
    from,
    to,
    viewport_width: typeof window === 'undefined' ? 0 : window.innerWidth,
  });
};
